import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import {
  p2pProviderPayinCustomerDailyLimitsApi,
  p2pProvidersApi,
  shopsApi,
} from 'api';
import {
  CopyText,
  CrudPage,
  DataGridColumnDefinition,
  DateLabel,
} from 'components';
import { FilterDefinitionType, QueryKey } from 'enums';
import { usePartialQuery, useShopsQuery, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  FilterDefinition,
  P2PProviderPayinCustomerDailyLimit,
  P2PProviderPayinCustomerDailyLimitsFilters,
} from 'types';
import { formUtils, formatUtils } from 'utils';

export const P2PProviderPayinCustomerDailyLimitsList: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.p2p_provider_payin_customer_daily_limits',
  });
  const { role } = useUser();

  const queryResult = usePartialQuery(
    QueryKey.P2PProviderPayinCustomerDailyLimits,
    p2pProviderPayinCustomerDailyLimitsApi.getAllPaginated,
  );
  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(role),
  );
  const queryResultProviders = useQuery(
    QueryKey.P2PProviders,
    p2pProvidersApi.getAll,
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<P2PProviderPayinCustomerDailyLimit>[] => [
      {
        header: t('fields.order_id'),
        valueGetter: (item) => <CopyText text={item.orderId} />,
        valueClassName: 'tw-font-mono tw-text-xs',
      },
      {
        header: t('fields.shop'),
        valueGetter: (item) =>
          formatUtils.formatName(item.order?.shop) || item.shopId,
      },
      {
        header: t('fields.customer_id'),
        valueGetter: (item) => <CopyText text={item.customerId} />,
      },
      {
        header: t('fields.provider'),
        valueGetter: (item) => item.provider?.name || item.providerId,
      },
      {
        header: t('fields.business_date'),
        valueKey: 'businessDate',
      },
      {
        header: t('fields.created_at'),
        valueGetter: (item) => (
          <DateLabel>{formatUtils.formatDate(item.createdAt)}</DateLabel>
        ),
      },
    ],
    [t],
  );

  const filtersDefinitions = useMemo(
    (): FilterDefinition<P2PProviderPayinCustomerDailyLimitsFilters>[] => [
      {
        label: t('filters.order_id'),
        name: 'orderId',
        type: FilterDefinitionType.Text,
        format: 'uuid',
      },
      {
        label: t('filters.shop'),
        name: 'shopId',
        type: FilterDefinitionType.Shop,
        shops: queryResultShops.data,
      },
      {
        label: t('filters.customer_id'),
        name: 'customerId',
        type: FilterDefinitionType.Text,
      },
      {
        label: t('filters.provider'),
        name: 'providerId',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(queryResultProviders.data || []),
      },
    ],
    [queryResultProviders.data, queryResultShops.data, t],
  );

  return (
    <CrudPage
      filters={{ filtersDefinitions }}
      table={{
        queryResult,
        columns,
        paginated: true,
      }}
    />
  );
};
